import { useEffect, useState } from "react";

import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useOptions, usePromoteObjective, type Note, type Workspace } from "../api";

const NONE = "__none__";

/** Selected initiative → quarterly objective in the Objectives tracker.
 *  Milestones and plans get built out over there — this creates the record
 *  and keeps the lineage on the note. */
export function PromoteObjectiveDialog({
  note,
  workspace,
  open,
  onOpenChange,
}: {
  note: Note;
  workspace: Workspace;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const options = useOptions();
  const promote = usePromoteObjective(workspace.session.id);
  const [quarterId, setQuarterId] = useState<string>("");
  const [ownerId, setOwnerId] = useState<string>(NONE);
  const [goalId, setGoalId] = useState<string>(NONE);

  useEffect(() => {
    if (open && options.data) {
      // Quarters arrive newest-first; the CURRENT quarter is the earliest open
      // one (the last open entry) — defaulting to the newest sent a batch of
      // offsite objectives to the wrong quarter once.
      const openQuarters = options.data.quarters.filter((q) => !q.is_closed);
      const current = openQuarters[openQuarters.length - 1];
      setQuarterId((prev) => prev || current?.id || "");
      setGoalId(note.goal_ids[0] ?? NONE);
    }
  }, [open, options.data, note.goal_ids]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Make “{note.title}” an objective</DialogTitle>
          <DialogDescription>
            Creates a quarterly objective in the Objectives tracker. Add milestones,
            owners per team, and the plan over there.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-muted-foreground">Quarter</div>
            <Select value={quarterId} onValueChange={setQuarterId}>
              <SelectTrigger>
                <SelectValue placeholder="Pick a quarter…" />
              </SelectTrigger>
              <SelectContent>
                {options.data?.quarters.map((q) => (
                  <SelectItem key={q.id} value={q.id} disabled={q.is_closed}>
                    {q.label}
                    {q.is_closed ? " (closed)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <div className="text-xs font-medium text-muted-foreground">Owner</div>
            <Select value={ownerId} onValueChange={setOwnerId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Decide later</SelectItem>
                {options.data?.users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <div className="text-xs font-medium text-muted-foreground">Company goal</div>
            <Select value={goalId} onValueChange={setGoalId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No goal link</SelectItem>
                {options.data?.goals.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.title} · {g.year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!quarterId || promote.isPending}
            onClick={() =>
              promote.mutate(
                {
                  id: note.id,
                  quarter_id: quarterId,
                  owner_id: ownerId === NONE ? null : ownerId,
                  goal_id: goalId === NONE ? null : goalId,
                },
                {
                  onSuccess: () => {
                    toast.success("Objective created — build it out in Objectives");
                    onOpenChange(false);
                  },
                  onError: (err) => toast.error(err.message),
                },
              )
            }
          >
            {promote.isPending ? "Creating…" : "Create objective"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
